import degreeToRadian from "common/utils/degreeToRadian";
import { getBSPAllocators } from "./bspMem";
import type { PlaneBSP } from "./bspTypes";
import type { Mat4 } from "./mat4";
import { mat4DotExt, mat4DotSub3, mat4Invert } from "./mat4";
import { planeNormal } from "./plane";
import type { Plane } from "./planeTypes";
import { vec2Normalize } from "./vec2";
import { vec3Cross, vec3Dot, vec3Normalize, vec3Sub } from "./vec3";

export function planeBSPNew(nX: number = -0, nY: number = -0, nZ: number = -0, dist: number = -0): PlaneBSP {
  const allocatorsBSP = getBSPAllocators();
  if (!allocatorsBSP) {
    return {
      nX,
      nY,
      nZ,
      dist,
    };
  }

  const plane = allocatorsBSP.allocPlane();

  plane.nX = nX;
  plane.nY = nY;
  plane.nZ = nZ;
  plane.dist = dist;

  return plane;
}

export function planeBSPCopy(plane: PlaneBSP): PlaneBSP {
  return planeBSPNew(plane.nX, plane.nY, plane.nZ, plane.dist);
}

export function planeBSPFlip({ nX, nY, nZ, dist }: PlaneBSP): PlaneBSP {
  return planeBSPNew(-nX, -nY, -nZ, -dist);
}

export function planeBSPTransform(plane: PlaneBSP, mat: Mat4): PlaneBSP {
  const { nX, nY, nZ, dist } = plane;

  const [x, y, z] = mat4DotExt(mat, nX * dist, nY * dist, nZ * dist);

  const [newNormalX, newNormalY, newNormalZ] = mat4DotSub3(mat, nX, nY, nZ);

  return planeBSPNew(newNormalX, newNormalY, newNormalZ, vec3Dot(x, y, z, newNormalX, newNormalY, newNormalZ));
}

export function planeBSPByThreePoints(
  originX: number,
  originY: number,
  originZ: number,
  aX: number,
  aY: number,
  aZ: number,
  bX: number,
  bY: number,
  bZ: number,
): PlaneBSP | null {
  const [uX, uY, uZ] = vec3Sub(aX, aY, aZ, originX, originY, originZ);
  const [heightdirx, heightdiry, heightdirz] = vec3Sub(bX, bY, bZ, originX, originY, originZ);

  let [fnormx, fnormy, fnormz] = vec3Cross(uX, uY, uZ, heightdirx, heightdiry, heightdirz);

  [fnormx, fnormy, fnormz] = vec3Normalize(fnormx, fnormy, fnormz);

  if (isNaN(fnormx)) {
    return null;
  }

  const dist = vec3Dot(fnormx, fnormy, fnormz, originX, originY, originZ);

  return planeBSPNew(fnormx, fnormy, fnormz, dist);
}

export function planeBSPByPointAndNormal(
  originX: number,
  originY: number,
  originZ: number,
  nX: number,
  nY: number,
  nZ: number,
): PlaneBSP {
  const dist = vec3Dot(nX, nY, nZ, originX, originY, originZ);

  return planeBSPNew(nX, nY, nZ, dist);
}

export function planeBSPFromPlane(plane: Plane): PlaneBSP {
  const [nX, nY, nZ] = planeNormal(plane);
  return planeBSPByPointAndNormal(plane.originX, plane.originY, plane.originZ, nX, nY, nZ);
}

export function planeBSPMoveAlongNormal({ nX, nY, nZ, dist }: PlaneBSP, offset: number): PlaneBSP {
  return planeBSPNew(nX, nY, nZ, dist + offset);
}

export function planeBSPMove({ nX, nY, nZ, dist }: PlaneBSP, x: number, y: number, z: number): PlaneBSP {
  const offset = vec3Dot(nX, nY, nZ, x, y, z);
  return planeBSPNew(nX, nY, nZ, dist + offset);
}

export function bspPerspectiveFrustum(
  viewMatrix: Mat4,
  fov: number,
  aspect: number,
  near: number,
  far: number,
): PlaneBSP[] {
  const halfFov = degreeToRadian(fov) * 0.5;
  const halfHorizontalFov = Math.atan(Math.tan(halfFov) * aspect);

  const cosH = Math.cos(halfHorizontalFov);
  const sinH = Math.sin(halfHorizontalFov);

  const cosV = Math.cos(halfFov);
  const sinV = Math.sin(halfFov);

  const invViewMatrix = mat4Invert(viewMatrix);

  return [
    planeBSPTransform(planeBSPNew(0, -cosV, sinV, 0), invViewMatrix), // down
    planeBSPTransform(planeBSPNew(0, cosV, sinV, 0), invViewMatrix), // up
    planeBSPTransform(planeBSPNew(-cosH, 0, sinH, 0), invViewMatrix), // left
    planeBSPTransform(planeBSPNew(cosH, 0, sinH, 0), invViewMatrix), // right
    planeBSPTransform(planeBSPNew(0, 0, 1, -near), invViewMatrix), // near
    planeBSPTransform(planeBSPNew(0, 0, -1, far), invViewMatrix), // far
  ];
}

/**
 * The left, right, top and bottom values represent the extents of the frustum at the near plane.
 */
export function bspPerspectiveCroppedFrustum(
  viewMatrix: Mat4,
  left: number,
  right: number,
  top: number,
  bottom: number,
  near: number,
  far: number,
): PlaneBSP[] {
  const invViewMatrix = mat4Invert(viewMatrix);

  const [bottomNormalY, bottomNormalZ] = vec2Normalize(-near, -bottom);
  const [topNormalY, topNormalZ] = vec2Normalize(near, top);
  const [leftNormalX, leftNormalZ] = vec2Normalize(-near, -left);
  const [rightNormalX, rightNormalZ] = vec2Normalize(near, right);

  // The clipping plane normals all face inwards.
  return [
    planeBSPTransform(planeBSPNew(0, bottomNormalY, bottomNormalZ, 0), invViewMatrix),
    planeBSPTransform(planeBSPNew(0, topNormalY, topNormalZ, 0), invViewMatrix),
    planeBSPTransform(planeBSPNew(leftNormalX, 0, leftNormalZ, 0), invViewMatrix),
    planeBSPTransform(planeBSPNew(rightNormalX, 0, rightNormalZ, 0), invViewMatrix),
    planeBSPTransform(planeBSPNew(0, 0, 1, -near), invViewMatrix), // near
    planeBSPTransform(planeBSPNew(0, 0, -1, far), invViewMatrix), // far
  ];
}

export function bspOrthogonalFrustum(
  viewMatrix: Mat4,
  length: number,
  aspect: number,
  near: number,
  far: number,
): PlaneBSP[] {
  const w = length * aspect * 0.5;
  const h = length * 0.5;

  const invViewMatrix = mat4Invert(viewMatrix);

  return [
    planeBSPTransform(planeBSPNew(0, -1, 0, h), invViewMatrix), // down
    planeBSPTransform(planeBSPNew(0, 1, 0, h), invViewMatrix), // up
    planeBSPTransform(planeBSPNew(-1, 0, 0, w), invViewMatrix), // left
    planeBSPTransform(planeBSPNew(1, 0, 0, w), invViewMatrix), // right
    planeBSPTransform(planeBSPNew(0, 0, 1, -near), invViewMatrix), // near
    planeBSPTransform(planeBSPNew(0, 0, -1, far), invViewMatrix), // far
  ];
}
